/* eslint-disable max-len */
import React, { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, ButtonVariant } from '../../../../common/Button'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { TextInput } from '../../../../common/TextInput'
import { useFormErrorMessage } from '../../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../../common/FormControl'
import { societyDocumentFolderCreateSchema } from '../../../../../forms/schemas/society_document_folder_create'
import { ISocietyWebsiteModel } from '../../../../../interfaces/models/societies-websites.interfaces'

interface CreateUpdateSocietyWebsiteFolderFormProps {
  onError: (error: unknown) => void
  onSubmit: (
    data: InferType<typeof societyDocumentFolderCreateSchema>
  ) => Promise<void>
  onClose: () => void
  loading: boolean
  folder?: NonNullable<ISocietyWebsiteModel['foldersList']>[0]
}

export const CreateUpdateSocietyWebsiteFolderForm = ({
  folder,
  loading,
  onError,
  onSubmit,
  onClose,
}: CreateUpdateSocietyWebsiteFolderFormProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()

  const updateMode = !!folder

  const getDefaultValues = useCallback((): InferType<
    typeof societyDocumentFolderCreateSchema
  > => {
    return {
      name: updateMode ? folder?.name || '' : '',
    }
  }, [updateMode, folder])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyDocumentFolderCreateSchema),
    defaultValues: getDefaultValues(),
  })

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <Controller
        control={control}
        name="name"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('createUpdateFolderForm.labels.name')}
            error={errors.name && getErrorMessage(errors.name)}
            name={name}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.name}
            />
          </FormControl>
        )}
      />
      <div className="mt-5 flex flex-shrink-0 flex-wrap items-center justify-end gap-3">
        <Button label={translate('common.actions.cancel')} onClick={onClose} />
        <Button
          loading={loading}
          variant={ButtonVariant.PRIMARY}
          disabled={!isValid || loading}
          label={translate('common.actions.save')}
          type="submit"
        />
      </div>
    </form>
  )
}
